import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { helpOf, withoutHelp, closeHelpWith, isChatSlug, ticketNumberOf, ticketSlug } from '@/helpers/helpHub'
import { HELP_HUB_QUERY, HELP_HUB_TAB, HELP_HUB_CHAT_PAGE, HELP_HUB_LAST_SLUG_KEY } from '@/constants'

// What the Help Hub shows is the route's `?help=<slug>`: a help page, `chat` for the Chat tab, one of its list pages or `ticket-<number>` for an open thread; none is the Help tab.
export function useHelpHub () {
  const route = useRoute()
  const router = useRouter()

  const helpSlug = computed(() => helpOf(route))
  const helpTab = computed(() => (isChatSlug(helpSlug.value) ? HELP_HUB_TAB.CHAT : HELP_HUB_TAB.HELP))
  // The open help page; null on the top level, whichever tab it shows, and on the Chat tab's list pages.
  const helpPage = computed(() => (helpTab.value === HELP_HUB_TAB.CHAT ? null : helpSlug.value))
  // The Chat tab's open list page: every conversation, ticket or question; null elsewhere.
  const chatPage = computed(() => (Object.values(HELP_HUB_CHAT_PAGE).includes(helpSlug.value) ? helpSlug.value : null))
  // The ticket number the URL names when the Chat tab shows a thread (`?help=ticket-32193`); null elsewhere.
  const ticketNumber = computed(() => ticketNumberOf(helpSlug.value))

  // The current app page with that slug in the URL, or with none when `slug` is null. A move inside the hub replaces the history entry: the site's Back is not the hub's.
  const helpLink = (slug) => ({
    path: route.path,
    query: { ...withoutHelp(route.query), ...(slug ? { [HELP_HUB_QUERY]: slug } : {}) },
    hash: route.hash,
    replace: true,
  })

  // An app page with the hub on `slug`, by default the page open now: naming it keeps the guard from the route's own page.
  const appLink = (to, slug = helpSlug.value) => {
    const { path, query, hash } = router.resolve(to)
    return { path, query: { ...query, ...(slug ? { [HELP_HUB_QUERY]: slug } : {}) }, hash }
  }

  // A null slug is the top level's Help tab.
  const openHelp = (slug) => (slug ? router.replace(helpLink(slug)) : closeHelpWith(() => router.replace(helpLink(null))))

  const openTab = (tab) => openHelp(tab === HELP_HUB_TAB.CHAT ? tab : null)

  // The thread of a ticket, by its number.
  const openTicket = (number) => openHelp(ticketSlug(number))

  // A closed hub shows nothing, so the URL stops naming a place in it; the next opening goes back there.
  const closeHelp = () => {
    if (!helpSlug.value) {
      localStorage.removeItem(HELP_HUB_LAST_SLUG_KEY)
      return
    }
    localStorage.setItem(HELP_HUB_LAST_SLUG_KEY, helpSlug.value)
    return openHelp(null)
  }

  // Puts the URL back on the place the hub was last closed on; false when another navigation overtook that move.
  const restoreHelp = async () => {
    const slug = localStorage.getItem(HELP_HUB_LAST_SLUG_KEY)
    if (!slug) return true
    await openHelp(slug)
    return helpSlug.value === slug
  }

  return { helpSlug, helpTab, helpPage, chatPage, ticketNumber, helpLink, appLink, openHelp, openTab, openTicket, closeHelp, restoreHelp }
}
